import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Info,
  Loader2,
  Plus,
  Settings as SettingsIcon,
  Tag as TagIconLucide,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  WizardStepIndicator,
  WizardStepDef,
} from "@/components/ui/wizard-step-indicator";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import {
  AppConfigTargetStep,
  AppConfigTargetValue,
  validateAppConfigTarget,
} from "./AppConfigTargetStep";
import { AppConfigKeyRow } from "./AppConfigKeyRow";
import {
  AppConfigKey,
  AppConfigSnapshot,
  emptyKeyRow,
} from "@/services/appConfigApi";
import {
  useAllAppConfigs,
  useAppConfigTags,
  useAvailableTags,
  useSaveAppConfig,
} from "@/hooks/useAppConfigurations";
import { TagIcon } from "@/components/tags/TagIcon";
import {
  findDuplicateKeyRowIds,
  validateConfigName,
  validateIdentifier,
  WIZARD_VALIDATION_MESSAGES,
} from "@/services/wizardValidation";
import { recommendTagsForApp } from "@/services/tagRecommendations";
import { useTagsDeviceCount } from "@/hooks/useTagsDeviceCount";
import { DeploymentSummaryPanel } from "@/components/applications/shared/DeploymentSummaryPanel";

import { ScopeAndAssignStep } from "@/components/shared/ScopeAndAssignStep";

type StepId = "target" | "keys" | "assignment";

const STEPS: WizardStepDef[] = [
  { id: "target", name: "Target App", description: "Pick or describe the app" },
  { id: "keys", name: "Configuration", description: "Define keys and values" },
  {
    id: "assignment",
    name: "Assignment",
    description: "Tags for deployment",
  },
];

interface AppConfigWizardProps {
  /** When provided, the wizard is locked to this app (no Step 1 mode toggle). */
  lockedTarget?: { appName: string; bundleId: string };
  /** Initial values for edit / duplicate. */
  initial?: {
    target?: Partial<AppConfigTargetValue>;
    configName?: string;
    keys?: AppConfigKey[];
    tagIds?: string[];
    existingPolicyId?: string;
  };
  /** Where to go after save / cancel. */
  returnTo?: string;
}

/**
 * Validate a row's *value* against its declared type. Mirrors the coercion
 * logic in `coerceValue` so the user sees the same errors inline that they
 * would otherwise hit at save time.
 */
function validateRowValue(row: AppConfigKey): string | undefined {
  if (!row.key.trim()) return undefined;
  if (row.type === "integer") {
    const trimmed = row.value.trim();
    if (trimmed === "") return "Must be a whole number.";
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return "Must be a whole number.";
    if (!Number.isInteger(n)) return "Integers must not have decimals.";
    return undefined;
  }
  if (row.type === "array") {
    const items = row.value
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
    if (items.length === 0) {
      return "Add at least one value (comma-separated).";
    }
    return undefined;
  }
  return undefined;
}

interface KeysErrors {
  configName?: string;
  rows?: { rowId: string; key?: string; value?: string }[];
  summary?: string;
}

/**
 * Validate the Configuration step. Uses the shared wizardValidation rules so
 * the contract matches PPPC: identifiers must start with a letter, max 64
 * characters, no forbidden substrings; duplicates are flagged on the second
 * (and later) occurrence; an empty list of usable rows is treated as a
 * summary-level error.
 */
function validateKeysStep(
  configName: string,
  keys: AppConfigKey[],
): KeysErrors {
  const errors: KeysErrors = {};
  const nameErr = validateConfigName(configName);
  if (nameErr) errors.configName = nameErr;

  const dupeRowIds = findDuplicateKeyRowIds(keys);
  const rowErrors: NonNullable<KeysErrors["rows"]> = [];
  let hasAnyKey = false;

  for (const row of keys) {
    const trimmed = row.key.trim();
    const rowErr: { rowId: string; key?: string; value?: string } = {
      rowId: row.rowId,
    };
    if (trimmed) {
      hasAnyKey = true;
      const keyErr = validateIdentifier(trimmed);
      if (keyErr) {
        rowErr.key = keyErr;
      } else if (dupeRowIds.has(row.rowId)) {
        rowErr.key = WIZARD_VALIDATION_MESSAGES.keyDuplicate;
      }
      const valueErr = validateRowValue(row);
      if (valueErr) rowErr.value = valueErr;
    }
    if (rowErr.key || rowErr.value) rowErrors.push(rowErr);
  }

  if (!hasAnyKey) {
    errors.summary = WIZARD_VALIDATION_MESSAGES.noKeys;
  } else if (rowErrors.length > 0) {
    errors.rows = rowErrors;
    errors.summary = WIZARD_VALIDATION_MESSAGES.fixHighlightedRows;
  }
  return errors;
}


export const AppConfigWizard = ({
  lockedTarget,
  initial,
  returnTo = "/configurations/app-config",
}: AppConfigWizardProps) => {
  const navigate = useNavigate();
  const save = useSaveAppConfig(initial?.target?.bundleId);
  const { data: allConfigs = [] } = useAllAppConfigs();

  const lockedSteps = !!lockedTarget;
  const [stepId, setStepId] = useState<StepId>(
    lockedSteps ? "keys" : "target",
  );
  const [completed, setCompleted] = useState<StepId[]>([]);

  const [target, setTarget] = useState<AppConfigTargetValue>(() => {
    if (lockedTarget) {
      return {
        mode: "catalog",
        catalogAppId: undefined,
        appName: lockedTarget.appName,
        bundleId: lockedTarget.bundleId,
      };
    }
    return {
      mode: initial?.target?.mode ?? "catalog",
      catalogAppId: initial?.target?.catalogAppId,
      appName: initial?.target?.appName ?? "",
      bundleId: initial?.target?.bundleId ?? "",
    };
  });

  const [configName, setConfigName] = useState(initial?.configName ?? "");
  const [keys, setKeys] = useState<AppConfigKey[]>(
    initial?.keys && initial.keys.length > 0
      ? initial.keys.map((k) => ({ ...k }))
      : [emptyKeyRow()],
  );
  const [tagIds, setTagIds] = useState<string[]>(
    (initial?.tagIds ?? []).slice().sort(),
  );
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [allCustomers, setAllCustomers] = useState(false);

  // Catalog targets (and locked-target callers, which are always catalog by
  // construction) inherit deployment from the app — the Assignment step has
  // nothing actionable to show, so we hide it entirely. External targets
  // delegate the customer → tag picker UI to the shared ScopeAndAssignStep.
  const showAssignmentStep = !lockedTarget && target.mode === "external";
  const visibleSteps = useMemo(() => {
    let steps = STEPS;
    if (lockedSteps) steps = steps.filter((s) => s.id !== "target");
    if (!showAssignmentStep) steps = steps.filter((s) => s.id !== "assignment");
    return steps;
  }, [lockedSteps, showAssignmentStep]);

  // If the user flips target.mode to catalog while sitting on the assignment
  // step (e.g. via the back→target nav), pop them back onto a visible step.
  useEffect(() => {
    if (!visibleSteps.find((s) => s.id === stepId)) {
      setStepId(visibleSteps[visibleSteps.length - 1].id as StepId);
    }
  }, [visibleSteps, stepId]);

  const [targetErrors, setTargetErrors] = useState<
    ReturnType<typeof validateAppConfigTarget>
  >({});
  const [keysErrors, setKeysErrors] = useState<KeysErrors>({});
  const [tagsErr, setTagsErr] = useState<string | undefined>();
  const [saveErr, setSaveErr] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const tagsCardRef = useRef<HTMLDivElement | null>(null);
  const saveErrorRef = useRef<HTMLDivElement | null>(null);

  const tagsQuery = useAvailableTags();
  const availableTags = tagsQuery.data ?? [];
  const [tagPickerOpen, setTagPickerOpen] = useState(false);

  // In edit mode, hydrate the tag baseline AND derive customer scope from the
  // tags' tenant ids the first time the existing assignment resolves.
  const existingTagsQuery = useAppConfigTags(initial?.existingPolicyId);
  const [hydratedTags, setHydratedTags] = useState(false);
  useEffect(() => {
    if (
      initial?.existingPolicyId &&
      !hydratedTags &&
      existingTagsQuery.data
    ) {
      const tags = existingTagsQuery.data;
      setTagIds(tags.map((t) => t.id).sort());
      const tenantIds = Array.from(
        new Set(
          tags
            .map((t) => t.tenantId)
            .filter((id): id is string => !!id),
        ),
      );
      if (tenantIds.length > 0) setSelectedCustomers(tenantIds);
      setHydratedTags(true);
    }
  }, [initial?.existingPolicyId, existingTagsQuery.data, hydratedTags]);

  // Bundle IDs that already have a config — surfaced as a hint in the picker.
  const existingBundleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of allConfigs) {
      if (initial?.existingPolicyId && c.policyId === initial.existingPolicyId)
        continue;
      ids.add(c.bundleId);
    }
    return ids;
  }, [allConfigs, initial?.existingPolicyId]);

  const stepIndex = visibleSteps.findIndex((s) => s.id === stepId);

  const markComplete = (id: StepId) =>
    setCompleted((prev) => (prev.includes(id) ? prev : [...prev, id]));

  const goNext = () => {
    if (stepId === "target") {
      const errs = validateAppConfigTarget(target);
      setTargetErrors(errs);
      if (Object.keys(errs).length) return;
      markComplete("target");
      setStepId("keys");
      return;
    }
    if (stepId === "keys") {
      const errs = validateKeysStep(configName, keys);
      setKeysErrors(errs);
      if (errs.summary || errs.configName) return;
      markComplete("keys");
      // Catalog targets skip Assignment entirely — go straight to save.
      if (!showAssignmentStep) {
        void handleSave();
        return;
      }
      setStepId("assignment");
      return;
    }
  };

  const goBack = () => {
    const idx = visibleSteps.findIndex((s) => s.id === stepId);
    if (idx > 0) setStepId(visibleSteps[idx - 1].id as StepId);
  };

  const toggleTag = (tagId: string) => {
    setTagIds((prev) => {
      const has = prev.includes(tagId);
      const next = has ? prev.filter((id) => id !== tagId) : [...prev, tagId];
      return next.sort();
    });
    if (tagsErr) setTagsErr(undefined);
  };

  const removeTag = (tagId: string) => {
    setTagIds((prev) => prev.filter((id) => id !== tagId));
  };

  const handleSave = async () => {
    // Re-entry guard: even though the Save button is disabled while
    // `submitting` is true, a fast double-click (or keyboard repeat) can
    // dispatch two clicks before React paints the disabled state. Bail
    // immediately on the second invocation.
    if (submitting) return;
    setSubmitting(true);
    setSaveErr(undefined);
    try {
      const tErr = validateAppConfigTarget(target);
      setTargetErrors(tErr);
      if (Object.keys(tErr).length) {
        setStepId(lockedSteps ? "keys" : "target");
        toast.error(WIZARD_VALIDATION_MESSAGES.fixHighlightedFieldsToast);
        return;
      }
      const kErr = validateKeysStep(configName, keys);
      setKeysErrors(kErr);
      if (kErr.summary || kErr.configName) {
        setStepId("keys");
        toast.error(WIZARD_VALIDATION_MESSAGES.fixHighlightedFieldsToast);
        return;
      }
      // Tags are required only for external targets. Catalog targets inherit
      // their deployment scope from the app itself, so an empty tag list is
      // an explicit "follow the app" — not a misconfiguration.
      const tagsRequired = !lockedTarget && target.mode === "external";
      if (tagsRequired && tagIds.length === 0) {
        setTagsErr(WIZARD_VALIDATION_MESSAGES.noTags);
        setStepId("assignment");
        toast.error(WIZARD_VALIDATION_MESSAGES.noTags);
        // Defer scroll so the assignment step has rendered.
        requestAnimationFrame(() => {
          tagsCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
        return;
      }
      setTagsErr(undefined);

      await save.mutateAsync({
        existingPolicyId: initial?.existingPolicyId,
        appName: target.appName || target.bundleId,
        bundleId: target.bundleId,
        configName: configName.trim(),
        keys: keys.filter((k) => k.key.trim().length > 0),
        tagIds,
        previousTagIds: hydratedTags
          ? (existingTagsQuery.data ?? []).map((t) => t.id).sort()
          : [],
      });
      navigate(returnTo);
    } catch (err) {
      // Surface API failure inline so the user knows what happened and can
      // retry without losing their input. Keep them on the current step.
      const raw = err instanceof Error ? err.message : String(err ?? "");
      let friendly: string;
      if (/failed to fetch|networkerror|load failed/i.test(raw)) {
        friendly = "We couldn't reach the server. Check your connection and try again.";
      } else if (/insertroot|duplicate|already exists|unique/i.test(raw)) {
        // Backend persistence collision — almost always a name clash on retry.
        friendly = `A configuration with this name already exists for this app. Try a different "Configuration name" and save again.`;
        setStepId("keys");
      } else {
        friendly = raw || "Failed to save app configuration. Please try again.";
      }
      setSaveErr(friendly);
      requestAnimationFrame(() => {
        saveErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Disable the Save button whenever we're either inside `handleSave` or
  // the underlying mutation is still in flight. Covers the edge case where
  // the React Query hook reports pending before our local flag updates
  // (e.g. when invoked programmatically).
  const isSaving = submitting || save.isPending;

  const isLastStep = stepId === visibleSteps[visibleSteps.length - 1].id;
  const liveKeysErrors = useMemo(
    () => validateKeysStep(configName, keys),
    [configName, keys],
  );
  const rowErrorById = new Map(
    (keysErrors.rows ?? liveKeysErrors.rows ?? []).map((e) => [e.rowId, e]),
  );

  const selectedTags = availableTags.filter((t) => tagIds.includes(t.id));
  const tagsDeviceCount = useTagsDeviceCount(tagIds);
  const recommendedTags = useMemo(
    () =>
      recommendTagsForApp(availableTags, target.appName).filter(
        (t) => !tagIds.includes(t.id),
      ),
    [availableTags, target.appName, tagIds],
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-background border-b">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => (stepIndex === 0 ? navigate(returnTo) : goBack())}
              disabled={isSaving}
              aria-label={
                stepIndex === 0 ? "Exit wizard" : "Go to previous step"
              }
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-base font-semibold leading-tight">
                {initial?.existingPolicyId
                  ? "Edit App Configuration"
                  : "New App Configuration"}
              </h1>
              <p className="text-xs text-muted-foreground leading-tight">
                Managed app configuration (key/value)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isLastStep ? (
              <Button onClick={goNext} disabled={isSaving}>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSave}
                disabled={isSaving}
                aria-busy={isSaving || undefined}
                aria-label={isSaving ? "Saving configuration" : "Save configuration"}
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {isSaving ? "Saving…" : "Save"}
              </Button>
            )}
          </div>
        </div>
        <WizardStepIndicator
          steps={visibleSteps}
          currentStep={stepId}
          completedSteps={completed}
        />
      </div>

      {/* Body */}
      <ScrollArea className="flex-1">
        <div className="px-6 py-8">
          {saveErr && (
            <div ref={saveErrorRef} className="max-w-3xl mx-auto mb-6">
              <Alert variant="destructive" role="alert" aria-label="Save error">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Couldn't save configuration</AlertTitle>
                <AlertDescription>
                  {saveErr} Your changes are still here — adjust if needed and click Save again.
                </AlertDescription>
              </Alert>
            </div>
          )}
          {stepId === "target" && (
            <AppConfigTargetStep
              value={target}
              onChange={setTarget}
              errors={targetErrors}
              locked={lockedSteps}
              existingBundleIds={existingBundleIds}
            />
          )}

          {stepId === "keys" && (
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="flex items-center gap-2">
                <SettingsIcon className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">Configuration</h2>
              </div>
              <p className="text-sm text-muted-foreground -mt-3">
                These values are pushed to the app's managed configuration
                dictionary (NSUserDefaults). The app developer defines which
                keys are supported.
              </p>

              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        App
                      </Label>
                      <Input
                        value={target.appName || target.bundleId || "—"}
                        readOnly
                        className="mt-1 bg-muted"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Bundle ID
                      </Label>
                      <Input
                        value={target.bundleId || "—"}
                        readOnly
                        className="mt-1 font-mono text-sm bg-muted"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="appcfg-config-name" className="text-sm">
                      Configuration name{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="appcfg-config-name"
                      value={configName}
                      onChange={(e) => setConfigName(e.target.value)}
                      placeholder="e.g. Production, Staging, EU Region"
                      className={
                        keysErrors.configName
                          ? "mt-1 border-destructive"
                          : "mt-1"
                      }
                      aria-invalid={!!keysErrors.configName || undefined}
                    />
                    {keysErrors.configName && (
                      <p className="text-xs text-destructive mt-1">
                        {keysErrors.configName}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Configuration Keys</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setKeys((prev) => [...prev, emptyKeyRow()])
                      }
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      Add Key
                    </Button>
                  </div>

                  {keysErrors.summary && !keysErrors.configName && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>
                        {keysErrors.rows && keysErrors.rows.length > 0
                          ? keysErrors.rows.length === 1
                            ? "Invalid configuration key"
                            : `${keysErrors.rows.length} invalid configuration keys`
                          : "Can't save yet"}
                      </AlertTitle>
                      <AlertDescription>{keysErrors.summary}</AlertDescription>
                    </Alert>
                  )}

                  {keys.length === 0 ? (
                    <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                      No keys yet. Click <strong>Add Key</strong> to start.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-[minmax(0,1fr)_120px_minmax(0,1.4fr)_auto] gap-2 px-1 text-xs font-medium text-muted-foreground">
                        <div>Key</div>
                        <div>Type</div>
                        <div>Value</div>
                        <div className="w-10" aria-hidden="true" />
                      </div>
                      {keys.map((row) => {
                        const rowErr = rowErrorById.get(row.rowId);
                        return (
                          <div key={row.rowId} className="space-y-1">
                            <AppConfigKeyRow
                              row={row}
                              onChange={(next) =>
                                setKeys((prev) =>
                                  prev.map((k) =>
                                    k.rowId === row.rowId ? next : k,
                                  ),
                                )
                              }
                              onRemove={() =>
                                setKeys((prev) =>
                                  prev.filter((k) => k.rowId !== row.rowId),
                                )
                              }
                              invalidKey={!!rowErr?.key}
                              invalidValue={!!rowErr?.value}
                            />
                            {(rowErr?.key || rowErr?.value) && (
                              <div className="grid grid-cols-[minmax(0,1fr)_120px_minmax(0,1.4fr)_auto] gap-2 px-1">
                                <p className="text-xs text-destructive">
                                  {rowErr?.key}
                                </p>
                                <span aria-hidden="true" />
                                <p className="text-xs text-destructive">
                                  {rowErr?.value}
                                </p>
                                <span aria-hidden="true" className="w-10" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <Info
                      className="h-3.5 w-3.5 mt-0.5 shrink-0"
                      aria-hidden="true"
                    />
                    Keys must start with a letter and contain only letters,
                    digits, and underscore (max 64 chars). Use
                    comma-separated values for arrays.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/*
           * Catalog targets skip the Assignment step entirely (their
           * deployment "follows the app"), so the Deployment Summary panel
           * would otherwise never render — leaving the user without a
           * single confirmation card and our tooltip tests with no surface
           * to assert against. Mount the same panel here in catalog mode,
           * passing `tagsRequired={false}` so it shows the "Follows the
           * app" subtitle and the catalog-flavored info tooltip instead of
           * the per-tag count math.
           */}
          {stepId === "keys" && target.mode !== "external" && (
            <div className="mt-6">
              <DeploymentSummaryPanel
                identity={[
                  { label: "App", value: target.appName ?? "" },
                  {
                    label: "Bundle ID",
                    value: target.bundleId ?? "",
                    mono: true,
                  },
                ]}
                customerCount={selectedCustomers.length}
                selectedTagNames={selectedTags.map((t) => t.name)}
                deviceCount={tagsDeviceCount}
                payloadCount={
                  keys.filter((k) => k.key.trim().length > 0).length
                }
                payloadLabel="Payloads"
                platformCount={1}
                tagsRequired={false}
                hideDraftAlert
              />
            </div>
          )}

          {stepId === "assignment" && (
            <div className="space-y-6" ref={tagsCardRef}>
              <ScopeAndAssignStep
                heading="Assignment"
                subtitle="Select customers to scope the available tags, then pick the tags whose devices should receive this configuration."
                hideAllCustomers
                hideSummary
                selectedCustomers={selectedCustomers}
                onCustomersChange={setSelectedCustomers}
                allCustomers={allCustomers}
                onAllCustomersChange={setAllCustomers}
                selectedTags={tagIds}
                onTagsChange={(next) => {
                  setTagIds(next);
                  if (tagsErr && next.length > 0) setTagsErr(undefined);
                }}
                payloadCount={keys.filter((k) => k.key.trim().length > 0).length}
                platformCount={1}
                policyId={initial?.existingPolicyId}
              />

              {/* Unified summary panel — matches ScopeAndAssignStep's 4-up
                  tile layout (Customers / Tags / Payloads / Platforms) and
                  carries the wizard-specific App + Bundle ID identity strip. */}
              <DeploymentSummaryPanel
                identity={[
                  { label: "App", value: target.appName ?? "" },
                  {
                    label: "Bundle ID",
                    value: target.bundleId ?? "",
                    mono: true,
                  },
                ]}
                customerCount={selectedCustomers.length}
                selectedTagNames={selectedTags.map((t) => t.name)}
                deviceCount={tagsDeviceCount}
                payloadCount={
                  keys.filter((k) => k.key.trim().length > 0).length
                }
                payloadLabel="Payloads"
                platformCount={1}
                hideDraftAlert
              />

              {tagsErr && (
                <Alert variant="destructive" role="alert" className="max-w-5xl mx-auto">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Select at least one tag</AlertTitle>
                  <AlertDescription>
                    {tagsErr}{" "}
                    {WIZARD_VALIDATION_MESSAGES.noTagsExternalDetailed}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
